import {
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  ScaleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import {
  type Attorney,
  attorneysApiClient,
} from '@/services/api/attorneysService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * Attorney entity type
 */
export type AttorneyEntity = Attorney;

/**
 * Extra props required for attorney link (needs firmId for query)
 */
export interface AttorneyLinkExtraProps {
  firmId: string;
}

/**
 * Format attorney address as a single line
 */
const formatAddress = (address: Attorney['address']): string | null => {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Attorney tooltip content component
 */
export const AttorneyTooltipContent = memo<
  EntityTooltipContentProps<AttorneyEntity>
>(({ fetchState }) => {
  if (fetchState.status === 'pending') {
    return <LoadingState rows={2} />;
  }

  if (fetchState.status === 'error') {
    return <ErrorState error={fetchState.error} entityType="attorney" />;
  }

  if (fetchState.status === 'idle') {
    return <EmptyState entityType="attorney" />;
  }

  const attorney = fetchState.data;
  const fullName = `${attorney.first_name} ${attorney.last_name}`;
  const formattedAddress = formatAddress(attorney.address);

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader
        icon={UserIcon}
        title={fullName}
        subtitle={attorney.title || undefined}
      />

      <TooltipSection withBorder>
        <InfoRow icon={EnvelopeIcon} label="Email" value={attorney.email} />
        {attorney.phone && (
          <InfoRow icon={PhoneIcon} label="Phone" value={attorney.phone} />
        )}
        {attorney.bar_number && (
          <InfoRow
            icon={ScaleIcon}
            label="Bar/Juris #"
            value={attorney.bar_number}
          />
        )}
        {formattedAddress && (
          <InfoRow icon={MapPinIcon} label="Address" value={formattedAddress} />
        )}
      </TooltipSection>
    </div>
  );
});

AttorneyTooltipContent.displayName = 'AttorneyTooltipContent';

/**
 * Hoverable attorney link with tooltip preview
 *
 * Shows attorney name, email, phone, bar number, and address on hover.
 * Does not navigate (attorneys don't have their own detail page).
 *
 * @example
 * ```tsx
 * <AttorneyLink entityId={attorneyId} firmId={firmId}>
 *   John Smith
 * </AttorneyLink>
 * ```
 */
export const AttorneyLink = createEntityLink<
  AttorneyEntity,
  { firm_id: string; id: string },
  AttorneyLinkExtraProps
>({
  tooltipContent: AttorneyTooltipContent,
  // No detail page for attorneys - tooltip only
  getDetailUrl: null,
  useQuery: attorneysApiClient.endpoints.getAttorney.useQuery,
  getQueryArg: (entityId, { firmId }) => ({ firm_id: firmId, id: entityId }),
  displayName: 'AttorneyLink',
});
